import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { isEkycVerified } from '@/store';
import { getKycColor, getKycLabel } from '@/utils/status';
import { getHtxById, updateHtxProfile, type BackendHtx } from '@/api/ekyc';
import { showToast } from '@/components/base/Toast';
import { confirmModal } from '@/components/base/ConfirmModal';
import { ApiError } from '@/api/client';

type EditableField = 'address' | 'phone' | 'email' | 'representativeName' | 'representativeTitle' | 'foundedDate' | 'memberCount' | 'mainCrops' | 'totalAreaHa';

export default function HtxProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [htx, setHtx] = useState<BackendHtx | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<BackendHtx>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.htxId) {
      setLoading(false);
      return;
    }
    getHtxById(user.htxId)
      .then((data) => {
        setHtx(data);
        setForm(data);
      })
      .catch(() => showToast('Không tải được hồ sơ HTX', 'error'))
      .finally(() => setLoading(false));
  }, [user?.htxId]);

  if (!user) return null;

  const ekycVerified = isEkycVerified(user);
  const kycLabel = getKycLabel(user.kycStatus || 'registered');

  const handleChange = (field: EditableField, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!htx) return;
    const ok = await confirmModal({
      title: 'Xác nhận cập nhật',
      message: 'Bạn có chắc muốn lưu thay đổi hồ sơ HTX?',
      confirmText: 'Lưu thay đổi',
      cancelText: 'Hủy',
      variant: 'primary',
    });
    if (!ok) return;

    setSaving(true);
    try {
      const updated = await updateHtxProfile(htx._id, {
        address: form.address,
        phone: form.phone,
        email: form.email,
        representativeName: form.representativeName,
        representativeTitle: form.representativeTitle,
        foundedDate: form.foundedDate,
        memberCount: form.memberCount !== undefined ? Number(form.memberCount) : undefined,
        mainCrops: form.mainCrops,
        totalAreaHa: form.totalAreaHa !== undefined ? Number(form.totalAreaHa) : undefined,
      });
      setHtx(updated);
      setForm(updated);
      setEditing(false);
      showToast('Đã cập nhật hồ sơ HTX', 'success');
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : 'Cập nhật thất bại, vui lòng thử lại', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (htx) setForm(htx);
    setEditing(false);
  };

  if (loading) {
    return <div className="px-4 py-8 text-center text-sm text-gray-400">Đang tải hồ sơ HTX...</div>;
  }

  if (!htx) {
    return <div className="px-4 py-8 text-center text-sm text-gray-400">Không tìm thấy hồ sơ HTX</div>;
  }

  const displayFields: { label: string; field: EditableField; type?: string }[] = [
    { label: 'Địa chỉ', field: 'address' },
    { label: 'Điện thoại', field: 'phone' },
    { label: 'Email', field: 'email' },
    { label: 'Người đại diện', field: 'representativeName' },
    { label: 'Chức danh người đại diện', field: 'representativeTitle' },
    { label: 'Ngày thành lập', field: 'foundedDate', type: 'date' },
    { label: 'Số thành viên', field: 'memberCount', type: 'number' },
    { label: 'Cây trồng chính', field: 'mainCrops' },
    { label: 'Tổng diện tích (ha)', field: 'totalAreaHa', type: 'number' },
  ];

  return (
    <div className="px-4 py-4 pb-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Hồ sơ HTX</h2>
          <p className="text-xs text-gray-500 mt-0.5">Thông tin hợp tác xã</p>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="px-3 py-2 rounded-xl text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
          >
            <i className="ri-edit-line mr-1" />
            Chỉnh sửa
          </button>
        )}
      </div>

      {/* ========== eKYC STATUS ========== */}
      <div className={`rounded-2xl border p-4 ${ekycVerified ? 'bg-gradient-to-br from-agrida-50 to-teal-50 border-agrida-200' : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`w-10 h-10 rounded-full flex items-center justify-center ${ekycVerified ? 'bg-agrida-100' : 'bg-amber-100'}`}>
              <i className={`${ekycVerified ? 'ri-shield-check-line text-agrida-600' : 'ri-shield-user-line text-amber-600'} text-lg`} />
            </span>
            <div>
              <p className={`text-sm font-bold ${ekycVerified ? 'text-agrida-800' : 'text-amber-800'}`}>{kycLabel}</p>
              <p className={`text-xs mt-0.5 ${ekycVerified ? 'text-agrida-700' : 'text-amber-700'}`}>
                {ekycVerified ? 'Danh tính đã được xác thực đầy đủ' : 'Cần xác thực để mở khóa chức năng tài chính'}
              </p>
            </div>
          </div>
          {!ekycVerified && (
            <button
              type="button"
              onClick={() => navigate('/ekyc')}
              className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-4 py-2.5 rounded-xl whitespace-nowrap transition-colors min-h-[44px] flex items-center gap-1.5"
            >
              <i className="ri-shield-user-line" />
              Thực hiện eKYC
            </button>
          )}
        </div>

        {user.kycReason && (
          <div className="mt-3 bg-white/60 rounded-xl p-3">
            <p className={`text-[10px] uppercase tracking-wider mb-0.5 ${ekycVerified ? 'text-agrida-700' : 'text-amber-700'}`}>Lý do</p>
            <p className={`text-xs ${ekycVerified ? 'text-agrida-700' : 'text-amber-700'}`}>{user.kycReason}</p>
          </div>
        )}

        {ekycVerified && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {user.creditScore !== undefined && (
              <div className="bg-white/70 rounded-xl p-3">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Credit Score</p>
                <p className={`text-sm font-bold ${user.creditScore >= 700 ? 'text-agrida-600' : user.creditScore >= 550 ? 'text-amber-600' : 'text-red-600'}`}>
                  {user.creditScore}
                </p>
              </div>
            )}
            {user.riskLevel && (
              <div className="bg-white/70 rounded-xl p-3">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Mức độ rủi ro</p>
                <p className="text-sm font-bold text-slate-800">{user.riskLevel}</p>
              </div>
            )}
          </div>
        )}

        {user.verifiedAt && (
          <p className={`text-[10px] text-right mt-2 ${ekycVerified ? 'text-agrida-700' : 'text-stone-500'}`}>
            Xác thực lúc: {new Date(user.verifiedAt).toLocaleDateString('vi-VN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>

      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-3">
          <span className="w-14 h-14 rounded-full bg-agrida-100 flex items-center justify-center">
            <i className="ri-building-line text-agrida-600 text-xl" />
          </span>
          <div>
            <p className="font-bold text-gray-900">{htx.name}</p>
            <p className="text-xs text-gray-500">Mã HTX: {htx.code}</p>
          </div>
        </div>
      </div>

      {editing ? (
        <div className="space-y-3">
          {displayFields.map((f) => (
            <div key={f.field}>
              <label className="block text-[11px] text-slate-400 mb-0.5">{f.label}</label>
              <input
                type={f.type || 'text'}
                value={form[f.field] ?? ''}
                onChange={(e) => handleChange(f.field, f.type === 'number' ? Number(e.target.value) : e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 focus:border-transparent"
              />
            </div>
          ))}
          <div className="flex flex-wrap gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2 rounded-xl text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px] disabled:opacity-50"
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-xl text-xs font-medium bg-agrida-600 hover:bg-agrida-700 text-white transition-colors whitespace-nowrap min-h-[44px] disabled:opacity-50"
            >
              {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <InfoRow icon="ri-map-pin-line" label="Địa chỉ" value={htx.address || '-'} />
          <InfoRow icon="ri-phone-line" label="Điện thoại" value={htx.phone || '-'} />
          <InfoRow icon="ri-mail-line" label="Email" value={htx.email || '-'} />
          <InfoRow icon="ri-user-star-line" label="Người đại diện" value={[htx.representativeName, htx.representativeTitle].filter(Boolean).join(' - ') || '-'} />
          <InfoRow icon="ri-calendar-line" label="Ngày thành lập" value={htx.foundedDate || '-'} />
          <InfoRow icon="ri-team-line" label="Số thành viên" value={htx.memberCount !== undefined ? `${htx.memberCount} nông hộ` : '-'} />
          <InfoRow icon="ri-seedling-line" label="Cây trồng chính" value={htx.mainCrops || '-'} />
          <InfoRow icon="ri-landscape-line" label="Tổng diện tích" value={htx.totalAreaHa !== undefined ? `${htx.totalAreaHa} ha` : '-'} />
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
      <span className="w-8 h-8 rounded-xl bg-agrida-100 flex items-center justify-center shrink-0">
        <i className={`${icon} text-agrida-600 text-sm`} />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] text-gray-400">{label}</p>
        <p className="text-sm font-medium text-gray-900">{value}</p>
      </div>
    </div>
  );
}
